const LETTER_SIZES = ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '2xl', '3xl'];

const WORD_SIZES = [
  'xx-small',
  'x-small',
  'xsmall',
  'extra small',
  'ekstra lille',
  'small',
  'lille',
  'medium',
  'mellem',
  'large',
  'stor',
  'x-large',
  'xlarge',
  'extra large',
  'ekstra stor',
  'xx-large',
  'xxlarge',
];

const FALLBACK_LOCALES = ['da', 'sv'];

const DEFAULT_SIZE_IDENTIFIERS = new Set(['size', 'storrelse', 'storlek']);

/**
 * @param {string} value
 * @returns {string}
 */
const normalize = (value) => value.trim().toLowerCase();

/**
 * Approximates Shopify's handleize for option name matching.
 * @param {string} value
 * @returns {string}
 */
export const handleize = (value) => value
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/ø/g, 'o')
  .replace(/æ/g, 'ae')
  .replace(/å/g, 'a')
  .replace(/ö/g, 'o')
  .replace(/ä/g, 'a')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

/**
 * @param {string} value
 * @returns {{ group: number, value: number | string }}
 */
const getSortKey = (value) => {
  const normalized = normalize(value);

  if (/^\d+(\.\d+)?$/.test(normalized)) {
    return { group: 0, value: parseFloat(normalized) };
  }

  const letterIndex = LETTER_SIZES.indexOf(normalized);
  if (letterIndex >= 0) {
    return { group: 1, value: letterIndex };
  }

  const wordIndex = WORD_SIZES.indexOf(normalized);
  if (wordIndex >= 0) {
    return { group: 2, value: wordIndex };
  }

  const leadingNumber = parseFloat(normalized);
  if (!Number.isNaN(leadingNumber)) {
    return { group: 0, value: leadingNumber };
  }

  return { group: 3, value: normalized };
};

/**
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const compareOptionValues = (a, b) => {
  const keyA = getSortKey(a);
  const keyB = getSortKey(b);

  if (keyA.group !== keyB.group) {
    return keyA.group - keyB.group;
  }

  if (typeof keyA.value === 'number' && typeof keyB.value === 'number') {
    return keyA.value - keyB.value;
  }

  return String(keyA.value).localeCompare(String(keyB.value), FALLBACK_LOCALES, {
    numeric: true,
    sensitivity: 'base',
  });
};

/**
 * @param {string[]} values
 * @returns {string[]}
 */
export const sortOptionValues = (values) => [...values].sort(compareOptionValues);

/**
 * @param {string | undefined | null} optionName
 * @returns {boolean}
 */
export const isSizeOption = (optionName) => {
  if (!optionName) return false;

  const sizeIdentifier = window.theme?.sizeIdentifier;
  const handleizedName = handleize(optionName);

  if (sizeIdentifier) {
    return handleizedName === sizeIdentifier;
  }

  return DEFAULT_SIZE_IDENTIFIERS.has(handleizedName);
};

/**
 * @param {HTMLElement} container
 * @param {(element: HTMLElement) => string} getValue
 */
export const applyFlexSortOrder = (container, getValue) => {
  const items = [...container.querySelectorAll('.radio-btn')];
  if (items.length < 2) return;

  const sorted = [...items].sort((a, b) => compareOptionValues(getValue(a), getValue(b)));
  sorted.forEach((element, index) => {
    element.style.order = String(index);
  });
};

/**
 * @param {HTMLSelectElement} select
 */
export const sortSelectOptions = (select) => {
  const options = [...select.options];
  if (options.length < 2) return;

  const selected = select.value;
  options.sort((a, b) => compareOptionValues(a.value, b.value));
  options.forEach((option) => select.appendChild(option));
  select.value = selected;
};

/**
 * @param {HTMLElement} element
 * @returns {string}
 */
const getRadioValue = (element) => {
  const input = element.querySelector('[data-option-value]');
  if (input?.dataset.optionValue) {
    return input.dataset.optionValue;
  }

  return element.querySelector('label')?.textContent?.trim() || '';
};

/**
 * @param {ParentNode} root
 */
export const sortVariantOptions = (root = document) => {
  root.querySelectorAll('[data-option-group][data-option-name]').forEach((group) => {
    if (!isSizeOption(group.dataset.optionName)) return;

    const container = group.querySelector('.flex.flex-wrap') || group;
    applyFlexSortOrder(container, getRadioValue);
  });

  root.querySelectorAll('[data-option-group]').forEach((group) => {
    if (group.dataset.optionName || !isSizeOption(group.dataset.optionGroup)) return;

    const container = group.querySelector('.flex.flex-wrap') || group;
    applyFlexSortOrder(container, getRadioValue);
  });

  root.querySelectorAll('.flex.flex-wrap').forEach((container) => {
    if (container.closest('[data-option-group]')) return;
    if (!container.querySelector('input[name="id"][type="radio"]')) return;

    applyFlexSortOrder(container, getRadioValue);
  });

  root.querySelectorAll('select[data-option-selector]:not(.hidden)').forEach((select) => {
    if (!isSizeOption(select.dataset.optionName)) return;
    sortSelectOptions(select);
  });
};
